import { useState } from "react";
import type { AnnotationRecordResponse } from "@/hooks/useLabelingQueries";
import { SavedSegmentationMeasurements } from "@/components/predictions/SavedSegmentationMeasurements";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";
import { formatSegmentationCondition } from "@/predictions/segmentationCondition";
import type { SegmentationPreview } from "@/predictions/segmentationPreviews";
import { READER_CONFIDENCE_KEY, parseReaderConfidence } from "@/utils/readerConfidence";
import { CardConfidenceRow } from "./CardConfidenceRow";

/**
 * A reader accepted the AI mask unchanged. Hue is redundant with the label, per the
 * verdict-scale exception in AGENTS.md: blue AI Prediction, purple AI accepted,
 * amber Modified.
 */
/** Disabled card actions look unavailable, not just unclickable (ENG-186). Pair with enabled:hover. */
export const DISABLED_ACTION_CLASS = "disabled:opacity-50 disabled:cursor-not-allowed";

export const AI_ACCEPTED_BADGE = {
  label: "AI accepted",
  className: "bg-purple-500/15 text-purple-300",
} as const;

export function SavedSegmentationCard({
  annotation,
  studyMetadata,
  conditionLabels = {},
  sequence,
  preview,
  thumbnail,
  selected = false,
  onSelect,
  onEdit,
  actionsDisabled = false,
  onReview,
  onSaveConfidence,
  editState,
}: {
  annotation: AnnotationRecordResponse;
  studyMetadata?: StudyMetadataResponse | null;
  conditionLabels?: Readonly<Record<string, string>>;
  sequence: number;
  preview?: SegmentationPreview | null;
  thumbnail?: string | null;
  selected?: boolean;
  onSelect?: (preview: SegmentationPreview) => void;
  onEdit?: () => void;
  actionsDisabled?: boolean;
  onReview?: (review: "accepted" | "declined" | "pending") => Promise<void>;
  /** Segmentation study: re-rate this card without the editor (ENG-277). */
  onSaveConfidence?: (readerConfidence: number) => Promise<void>;
  /** While an editor is open: this card is being edited, or another one is. */
  editState?: "active" | "dimmed";
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const review = annotation.attributes.manual_annotation_review;
  const applyReview = async (next: "accepted" | "declined" | "pending") => {
    if (!onReview || pending || actionsDisabled) return;
    setPending(true);
    setError(null);
    try {
      await onReview(next);
    } catch {
      setError("Could not save review. Please retry.");
    } finally {
      setPending(false);
    }
  };
  const label = formatSegmentationCondition(
    conditionLabels[annotation.category] ??
      annotation.attributes.ai_prediction_condition ??
      annotation.category,
  );
  const series =
    studyMetadata?.series_metadata[annotation.series_instance_uid]?.series_number ?? "—";
  const slice = preview?.sliceNumber ?? "—";
  const navigable = !!preview && !!onSelect;
  const isAi = typeof annotation.attributes.ai_prediction_condition === "string";
  const aiAccepted = isAi && annotation.attributes.ai_prediction_review === "accepted";
  return (
    <article
      role="button"
      tabIndex={navigable ? 0 : -1}
      aria-disabled={!navigable}
      onClick={() => {
        if (preview) onSelect?.(preview);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (preview) onSelect?.(preview);
        }
      }}
      aria-label={`${label} ${sequence}, saved annotation`}
      data-testid="saved-segmentation-card"
      data-editing={editState === "active" || undefined}
      className={`flex gap-3 p-2.5 text-left bg-surface border rounded-[2px] transition-[border-color,opacity] ${
        editState === "active"
          ? "border-signal border-l-4"
          : selected
            ? "border-signal"
            : "border-border"
      } ${editState === "dimmed" ? "opacity-40" : ""}`}
    >
      <div className="shrink-0 w-[132px] h-[132px] rounded-[2px] bg-bg-2 border border-dashed border-border-strong flex items-center justify-center">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={`${label} saved segmentation preview on slice ${slice}`}
            className="w-full h-full"
          />
        ) : (
          <span className="font-mono text-[10px] text-fg-muted">
            {preview && thumbnail !== null ? "Loading preview…" : "Preview unavailable"}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-xs text-fg">
            {label} · {sequence}
          </span>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
              aiAccepted
                ? AI_ACCEPTED_BADGE.className
                : isAi
                  ? "bg-surface-2 text-fg-muted"
                  : "bg-green-500/15 text-green-400"
            }`}
          >
            {isAi && annotation.attributes.ai_prediction_review === "modified"
              ? "Modified"
              : aiAccepted
                ? AI_ACCEPTED_BADGE.label
                : isAi
                  ? "Saved annotation"
                  : "Manual"}
          </span>
        </div>
        <SavedSegmentationMeasurements annotation={annotation} />
        <div className="font-mono text-[11px] text-fg-muted">
          Ser {series} · Slice {slice}
        </div>
        <div className="font-mono text-[10px] text-fg-muted">Saved segment {sequence}</div>
        {/* While this card is edited, the rating lives in the editor. */}
        {onSaveConfidence && editState !== "active" && (
          <CardConfidenceRow
            value={parseReaderConfidence(annotation.attributes[READER_CONFIDENCE_KEY])}
            // Same gate as this card's Accept and Edit.
            disabled={actionsDisabled || pending || !onEdit}
            onSave={onSaveConfidence}
          />
        )}
        {(onReview || onEdit) && (
          <div
            className="mt-auto flex flex-wrap gap-1.5 pt-1"
            onClick={(event) => event.stopPropagation()}
          >
            {onReview && (
              <button
                type="button"
                aria-label={
                  review === "accepted" ? "Undo accepted segmentation" : "Accept segmentation"
                }
                aria-pressed={review === "accepted"}
                disabled={actionsDisabled || pending}
                className={`h-6 px-2 whitespace-nowrap rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${DISABLED_ACTION_CLASS} ${review === "accepted" ? "bg-signal text-white border-signal" : "border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2"}`}
                onClick={() => void applyReview(review === "accepted" ? "pending" : "accepted")}
              >
                {review === "accepted" ? "Undo accept" : "Accept"}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                aria-label="Edit segmentation"
                disabled={actionsDisabled || pending}
                className={`h-6 px-2 whitespace-nowrap rounded-[2px] border border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2 ${DISABLED_ACTION_CLASS} font-mono text-[10px] uppercase tracking-wide`}
                onClick={onEdit}
              >
                Edit
              </button>
            )}
            {onReview && (
              <button
                type="button"
                aria-label={
                  review === "declined" ? "Undo declined segmentation" : "Decline segmentation"
                }
                aria-pressed={review === "declined"}
                disabled={actionsDisabled || pending}
                className={`h-6 px-2 whitespace-nowrap rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${DISABLED_ACTION_CLASS} ${review === "declined" ? "bg-warn-tint text-warn border-warn/50" : "border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2"}`}
                onClick={() => void applyReview(review === "declined" ? "pending" : "declined")}
              >
                {review === "declined" ? "Undo decline" : "Decline"}
              </button>
            )}
          </div>
        )}
        {onReview && (review === "accepted" || review === "declined") && (
          <span className="text-[10px] text-fg-muted">
            {review === "accepted" ? "Accepted" : "Declined"}
          </span>
        )}
        {error && (
          <span role="alert" className="text-[10px] text-red-400">
            {error}
          </span>
        )}
        <div className="font-mono text-[9px] uppercase tracking-wide text-fg-muted">
          {preview ? "Click to open key slice" : "Saved geometry unavailable"}
        </div>
      </div>
    </article>
  );
}
